import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { X, Loader2, Shield, ArrowLeft } from 'lucide-react'
import { useI18n } from '../../i18n'
import { usePlan } from '../../hooks/use-plan'
import { useAuth } from '../../hooks/use-auth'
import { PaymentMethodSelector } from './PaymentMethodSelector'
import { PaymentQRCode } from './PaymentQRCode'
import { PaymentResult } from './PaymentResult'
import type { Plan, PayChannel, PayStatus, BillingCycle } from '../../types'

type DialogStep = 'preview' | 'method-select' | 'qr-display' | 'result'

interface PaymentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  plan: Plan
  onSuccess: () => void
}

const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)

function getExpireSeconds(expireTime: string): number {
  const expire = new Date(expireTime).getTime()
  const now = Date.now()
  return Math.max(0, Math.floor((expire - now) / 1000))
}

function formatTokenQuota(n: number, lang: string) {
  if (n >= 10000000) return lang === 'zh' ? `${(n / 10000000).toFixed(0)}千万` : `${(n / 1000000).toFixed(0)}M`
  if (n >= 10000) return lang === 'zh' ? `${(n / 10000).toFixed(0)}万` : `${(n / 1000).toFixed(0)}K`
  return n.toLocaleString()
}

export function PaymentDialog({ open, onOpenChange, plan, onSuccess }: PaymentDialogProps) {
  const { lang, t } = useI18n()
  const { createOrder } = usePlan()
  const { refreshUserInfo } = useAuth()

  const [step, setStep] = useState<DialogStep>('preview')
  const [channel, setChannel] = useState<PayChannel>('ALIPAY')
  const [cycle, setCycle] = useState<BillingCycle>('MONTHLY')
  const [orderNo, setOrderNo] = useState('')
  const [qrCodeUrl, setQrCodeUrl] = useState('')
  const [expireSeconds, setExpireSeconds] = useState(1800)
  const [resultStatus, setResultStatus] = useState<PayStatus>('PENDING')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [visible, setVisible] = useState(false)
  const [contentKey, setContentKey] = useState(0)
  const contentRef = useRef<HTMLDivElement>(null)

  const isTopup = plan.planCode.startsWith('TOPUP')
  const isFree = plan.planCode === 'FREE'
  const displayPrice = isFree ? 0 : cycle === 'YEARLY' && plan.priceYearly !== null ? plan.priceYearly : plan.priceMonthly
  const priceLabel = displayPrice === 0
    ? t('免费', 'Free')
    : `¥${displayPrice.toLocaleString()}`

  // 打开/关闭动画 + 锁滚动
  useEffect(() => {
    if (open) {
      setStep('preview')
      setChannel('ALIPAY')
      setCycle('MONTHLY')
      setOrderNo('')
      setQrCodeUrl('')
      setResultStatus('PENDING')
      setError('')
      document.body.style.overflow = 'hidden'
      requestAnimationFrame(() => setVisible(true))
    } else {
      setVisible(false)
      const timer = setTimeout(() => { document.body.style.overflow = '' }, 300)
      return () => clearTimeout(timer)
    }
    return () => { document.body.style.overflow = '' }
  }, [open])

  // 步骤切换动画
  const changeStep = (newStep: DialogStep) => {
    setContentKey((k) => k + 1)
    setStep(newStep)
  }

  const handleConfirmAndPay = async () => {
    setLoading(true)
    setError('')

    try {
      const request: any = {
        planCode: plan.planCode,
        payChannel: channel,
      }
      if (!isTopup) {
        request.billingCycle = cycle
      }

      const order = await createOrder(request)

      if (!order) {
        setError(t('创建订单失败，请重试', 'Failed to create order'))
        setLoading(false)
        return
      }

      setOrderNo(order.orderNo)
      setQrCodeUrl(order.qrCodeUrl)
      setExpireSeconds(getExpireSeconds(order.expireTime))

      if (isMobile) {
        window.location.href = order.qrCodeUrl
      } else {
        changeStep('qr-display')
      }
    } catch {
      setError(t('网络错误，请重试', 'Network error'))
    }

    setLoading(false)
  }

  const handleStatusChange = (status: PayStatus) => {
    setResultStatus(status)
    changeStep('result')
    if (status === 'PAID') {
      onSuccess()
      refreshUserInfo()
    }
  }

  const handleRetry = () => {
    setOrderNo('')
    setQrCodeUrl('')
    setResultStatus('PENDING')
    changeStep('preview')
  }

  if (!open) return null

  const dialog = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ transition: 'none' }}
    >
      {/* 遮罩：淡入淡出 */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300"
        style={{ opacity: visible ? 1 : 0 }}
        onClick={() => onOpenChange(false)}
      />

      {/* 弹窗：缩放 + 淡入 */}
      <div
        className="relative w-full max-w-md bg-white rounded-2xl border border-black/[0.06] shadow-[0_24px_48px_-12px_rgba(0,0,0,0.18)] overflow-hidden"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'scale(1) translateY(0)' : 'scale(0.95) translateY(8px)',
          transition: 'opacity 300ms cubic-bezier(0.16,1,0.3,1), transform 300ms cubic-bezier(0.16,1,0.3,1)',
        }}
      >
        {/* 顶部装饰光 */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-1 rounded-b-full bg-gradient-to-r from-orange-400 via-amber-400 to-orange-400 opacity-60" />

        {/* 头部 */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4">
          <div className="flex items-center gap-2">
            {step === 'method-select' && (
              <button
                onClick={() => changeStep('preview')}
                className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-all duration-200"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
            )}
            <h2 className="text-sm font-semibold text-[#1a1a1a]">
              {step === 'preview' && t('订单预览', 'Order Preview')}
              {step === 'method-select' && t('选择支付方式', 'Payment Method')}
              {step === 'qr-display' && t('扫码支付', 'Scan to Pay')}
              {step === 'result' && t('支付结果', 'Payment Result')}
            </h2>
          </div>
          <button
            onClick={() => onOpenChange(false)}
            className="p-1.5 rounded-lg text-gray-300 hover:text-gray-500 hover:bg-gray-50 transition-all duration-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* 内容区：步骤切换动画 */}
        <div
          ref={contentRef}
          key={contentKey}
          className="px-5 pb-5"
          style={{
            animation: 'fadeSlideIn 280ms cubic-bezier(0.16,1,0.3,1)',
          }}
        >
          {error && (
            <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-100 text-red-500 text-xs">
              {error}
            </div>
          )}

          {/* Step 1: 订单预览 */}
          {step === 'preview' && (
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-gradient-to-br from-orange-50/60 to-amber-50/30 border border-orange-100/50">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-[#1a1a1a]">{plan.name}</span>
                  <span className="text-lg font-bold text-orange-500">{priceLabel}</span>
                </div>
                <div className="space-y-1.5 text-xs text-gray-400">
                  <div className="flex justify-between">
                    <span>{t('Token 额度', 'Token Quota')}</span>
                    <span className="text-gray-600">{formatTokenQuota(plan.tokenQuota, lang)}/{isTopup ? t('次', 'time') : t('月', 'mo')}</span>
                  </div>
                  {!isTopup && plan.maxUsers > 0 && (
                    <div className="flex justify-between">
                      <span>{t('最大用户数', 'Max Users')}</span>
                      <span className="text-gray-600">{plan.maxUsers}</span>
                    </div>
                  )}
                  {plan.description && (
                    <div className="flex justify-between">
                      <span>{t('说明', 'Description')}</span>
                      <span className="text-gray-600">{plan.description}</span>
                    </div>
                  )}
                </div>
              </div>

              {!isTopup && plan.priceYearly !== null && plan.priceMonthly > 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-gray-400">{t('计费周期', 'Billing Cycle')}</p>
                  <div className="relative flex rounded-xl bg-gray-100 p-1">
                    {/* 滑块指示器 */}
                    <div
                      className="absolute top-1 bottom-1 w-[calc(50%-4px)] rounded-lg bg-white shadow-sm transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]"
                      style={{ transform: cycle === 'YEARLY' ? 'translateX(100%)' : 'translateX(0)' }}
                    />
                    <button
                      onClick={() => setCycle('MONTHLY')}
                      className={`relative z-10 flex-1 py-2.5 rounded-lg text-center transition-colors duration-300 ${
                        cycle === 'MONTHLY' ? 'text-[#1a1a1a]' : 'text-gray-400'
                      }`}
                    >
                      <p className={`text-sm font-medium transition-colors duration-300 ${cycle === 'MONTHLY' ? 'text-[#1a1a1a]' : 'text-gray-400'}`}>{t('月付', 'Monthly')}</p>
                      <p className={`text-xs transition-colors duration-300 ${cycle === 'MONTHLY' ? 'text-orange-500' : 'text-gray-400'}`}>¥{plan.priceMonthly.toLocaleString()}/{t('月', 'mo')}</p>
                    </button>
                    <button
                      onClick={() => setCycle('YEARLY')}
                      className={`relative z-10 flex-1 py-2.5 rounded-lg text-center transition-colors duration-300 ${
                        cycle === 'YEARLY' ? 'text-[#1a1a1a]' : 'text-gray-400'
                      }`}
                    >
                      <p className={`text-sm font-medium transition-colors duration-300 ${cycle === 'YEARLY' ? 'text-[#1a1a1a]' : 'text-gray-400'}`}>{t('年付', 'Yearly')}</p>
                      <p className={`text-xs transition-colors duration-300 ${cycle === 'YEARLY' ? 'text-emerald-500' : 'text-gray-400'}`}>¥{plan.priceYearly!.toLocaleString()}/{t('年', 'yr')}</p>
                    </button>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50/50">
                <Shield className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                <span className="text-[11px] text-emerald-600">{t('支付安全由支付宝/微信保障', 'Secure payment via Alipay/WeChat')}</span>
              </div>

              <button
                onClick={() => changeStep('method-select')}
                className="w-full h-10 bg-orange-500 hover:bg-orange-600 active:scale-[0.98] text-white text-sm font-medium rounded-xl transition-all duration-200"
              >
                {t('选择支付方式', 'Choose Payment Method')}
              </button>
            </div>
          )}

          {/* Step 2: 选择支付方式 */}
          {step === 'method-select' && (
            <div className="space-y-4">
              <div className="p-3 rounded-xl bg-orange-50/50 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-[#1a1a1a]">{plan.name}</p>
                  <p className="text-xs text-gray-400">
                    {cycle === 'YEARLY' && !isTopup ? t('年付', 'Yearly') : isTopup ? t('加购包', 'Top-up') : t('月付', 'Monthly')}
                  </p>
                </div>
                <span className="text-lg font-bold text-orange-500">{priceLabel}</span>
              </div>

              <PaymentMethodSelector value={channel} onChange={setChannel} />

              <button
                onClick={handleConfirmAndPay}
                disabled={loading}
                className="w-full h-10 bg-[#1a1a1a] hover:bg-[#333] active:scale-[0.98] disabled:bg-gray-300 disabled:active:scale-100 text-white text-sm font-medium rounded-xl transition-all duration-200 flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    {t('创建订单中...', 'Creating order...')}
                  </>
                ) : (
                  t('确认支付', 'Confirm Payment')
                )}
              </button>
            </div>
          )}

          {/* Step 3: 扫码支付 */}
          {step === 'qr-display' && (
            <PaymentQRCode
              orderId={orderNo}
              qrCodeUrl={qrCodeUrl}
              expireSeconds={expireSeconds}
              channel={channel}
              onStatusChange={handleStatusChange}
            />
          )}

          {/* Step 4: 结果 */}
          {step === 'result' && (
            <PaymentResult
              status={resultStatus}
              onRetry={handleRetry}
              onClose={() => onOpenChange(false)}
            />
          )}
        </div>
      </div>

      {/* 内联 keyframes */}
      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateX(12px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  )

  return createPortal(dialog, document.body)
}
